import { useMemo } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import useFormatDateTime from '@/hooks/format/useFormatDateTime';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
// Components
import TooltipCustom from './components/TooltipCustom';

import ChartWrapper from './ChartWrapper';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomHourAxisTick = ({ x, y, payload, format, isExport }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={10}
                textAnchor="end"
                transform="rotate(-45)"
                className={clsx('fill-current font-GSemiBold text-xxs text-icoGray-500', {
                    'dark:text-icoDarkMode-wolf': !isExport
                })}>
                {format ? format(payload.value) : payload.value}
            </text>
        </g>
    );
};

CustomHourAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.object,
    format: PropTypes.func,
    isExport: PropTypes.bool
};

const HalfBarChartComponent = ({
    data,
    activeTable,
    columnNames,
    className,
    userNumbersFormat,
    activeFilter,
    isExport,
    hiddenKeys,
    height = 230
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const isDateChart = useMemo(() => Object.keys(data.name)[0] === 'date', [data]);
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTimeNoTz } = useFormatDateTime(userNumbersFormat?.date?.timezone);

    const ignoredData = useMemo(
        () => [
            'age',
            'date',
            'total',
            'month',
            'week',
            'percentage_male',
            'percentage_female',
            'type'
        ],
        []
    );

    const finalData = useMemo(() => {
        let finalData = [];
        switch (data.id) {
            case 'page_likes_history':
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        unlikes: item.unlikes ? `-${item.unlikes}` : item.unlikes,
                        total: item.likes - item.unlikes
                    });
                });

                return finalData;
            case 'ages_genders_distribution':
            case 'reached_audience_demographics_distribution':
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        female: item?.female ? `-${item?.female}` : item?.female
                    });
                });

                return finalData;
            case 'page_views_history':
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        total: item.logged + item.not_logged
                    });
                });

                return finalData;

            case 'post_engagements_history':
                const acceptableDataProperties = [
                    'reactions',
                    'clicks',
                    'shares',
                    'comments',
                    'likes',
                    'saves'
                ];
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        total: Object.keys(item).reduce(
                            (accum, currentValue) =>
                                acceptableDataProperties.includes(currentValue)
                                    ? accum + item[currentValue]
                                    : accum,
                            0
                        )
                    });
                });

                return finalData;

            case 'page_cta_clicks_history':
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        total: item.call_now + item.directions + item.website
                    });
                });

                return finalData;
            case 'average_reach_per_post_type_distribution':
                const existingDataValuesProperties = [];
                if (data.dataValuesProperties) {
                    data.dataValuesProperties.forEach((dataValueProperty) => {
                        if (Object.keys(data.graphData[0]).includes(dataValueProperty)) {
                            existingDataValuesProperties.push(dataValueProperty);
                        }
                    });
                }
                if (existingDataValuesProperties.length > 1) {
                    data.graphData.forEach((item) => {
                        let total = 0;
                        existingDataValuesProperties.forEach((existingDataValueProperty) => {
                            total += item[existingDataValueProperty];
                        });
                        finalData.push({
                            ...item,
                            total: total
                        });
                    });

                    return finalData;
                } else {
                    return data.graphData;
                }
            default:
                return data.graphData;
        }
    }, [data.graphData, data.id]);

    const finalColumnNames = useMemo(() => {
        if (finalData.length) {
            let obj = {};
            switch (data.id) {
                case 'page_likes_history':
                case 'page_views_history':
                case 'post_engagements_history':
                case 'average_reach_per_post_type_distribution':
                    Object.entries(finalData[0])?.map((item) => {
                        obj[item[0]] = data.name[item[0]] ? t(data.name[item[0]]) : item[0];
                    });

                    return obj;
                case 'posts_distribution':
                    Object.entries(finalData[0])?.map((item) => {
                        obj[item[0]] =
                            data.name[item[0]] === 'month' || data.name[item[0]] === 'week'
                                ? t(data.name[item[0]])
                                : data.name[item[0]] ?? item[0];
                    });

                    return obj;

                case 'page_cta_clicks_history':
                    obj = {
                        ...columnNames,
                        total: t('total')
                    };

                    return obj;
                default:
                    return columnNames;
            }
        }

        return columnNames;
    }, [data.id, data.name, columnNames, finalData, t]);

    const finalConfig = useMemo(() => {
        let finalConfig = {
            ...data
        };
        switch (data.id) {
            case 'posts_distribution':
                Object.keys(finalData[0]).map((item, index) => {
                    if (item !== 'month' && item !== 'week') {
                        finalConfig.color[item] = finalConfig.valueColors[index];
                        finalConfig.name[item] = item;
                    }
                });
                return finalConfig;
            default:
                return data;
        }
    }, [data, finalData]);

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={finalData}
                    graphId={data.id}
                    columnNames={finalColumnNames}
                    valueFormat={{ signDisplay: 'never' }}
                    timeFormat={data.timeFormatter}
                    userNumbersFormat={userNumbersFormat}
                    formatterTranslation={data.tableColumnFormatterTranslation}
                />
            ) : (
                <SizeMe>
                    {({ size }) => (
                        <div
                            className={clsx('relative w-full overflow-x-auto lg:h-full', className)}
                            style={{ minHeight: isExport ? 180 : height }}>
                            <div className="absolute left-0 top-0 h-full w-full">
                                <ResponsiveContainer
                                    height={isExport ? 180 : height}
                                    width={isExport ? data.fixedSize : '100%'}
                                    minWidth={data.minWidth}>
                                    <BarChart
                                        data={finalData}
                                        stackOffset="sign"
                                        barSize={
                                            data.barSize && activeFilter !== 'week'
                                                ? data.barSize
                                                : 10
                                        }
                                        layout={data.orientation}
                                        margin={data.margin}>
                                        <CartesianGrid
                                            vertical={data.orientation === 'vertical'}
                                            horizontal={data.orientation === 'horizontal'}
                                            stroke={darkModeColorsChart[theme].grid}
                                        />
                                        <XAxis
                                            dataKey={
                                                data.orientation === 'horizontal' &&
                                                (data.filters
                                                    ? activeFilter
                                                    : Object.keys(data.name)[0])
                                            }
                                            type={
                                                data.orientation === 'horizontal'
                                                    ? 'category'
                                                    : 'number'
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            tick={
                                                (finalData.length > 8 || size.width < 270) &&
                                                isDateChart ? (
                                                    <CustomHourAxisTick
                                                        isExport={isExport}
                                                        format={(item) =>
                                                            isDateChart
                                                                ? formatDateTimeNoTz(
                                                                      item,
                                                                      userNumbersFormat?.date
                                                                          ?.format
                                                                  )
                                                                : data?.tickFormatterTranslation &&
                                                                    activeFilter !== 'week'
                                                                  ? t(
                                                                        `${data.tickFormatterTranslation}.${item}`
                                                                    ).substring(0, 3)
                                                                  : formatNumber(
                                                                        item,
                                                                        data.valueFormat
                                                                    )
                                                        }
                                                    />
                                                ) : (
                                                    {}
                                                )
                                            }
                                            tickFormatter={
                                                finalData.length <= 8 || size.width > 270
                                                    ? (item) => {
                                                          if (isDateChart) {
                                                              return formatDateTimeNoTz(
                                                                  item,
                                                                  userNumbersFormat?.date?.format
                                                              );
                                                          }
                                                          if (
                                                              data?.tickFormatterTranslation &&
                                                              activeFilter !== 'week'
                                                          ) {
                                                              const tickLabel = t(
                                                                  `${data.tickFormatterTranslation}.${item}`
                                                              );
                                                              return activeFilter === 'month'
                                                                  ? tickLabel.substring(0, 3)
                                                                  : tickLabel;
                                                          } else {
                                                              return formatNumber(
                                                                  item,
                                                                  data.valueFormat
                                                              );
                                                          }
                                                      }
                                                    : null
                                            }
                                            minTickGap={isDateChart && -50}
                                            interval={
                                                // responsive xAxis labels
                                                isDateChart &&
                                                data.graphData.length * 20 > size.width
                                                    ? Math.round(
                                                          data.graphData.length / (size.width / 20)
                                                      )
                                                    : 0
                                            }
                                        />
                                        <YAxis
                                            dataKey={
                                                data.orientation === 'vertical' &&
                                                Object.keys(data.name)[0]
                                            }
                                            tickCount={5}
                                            type={
                                                data.orientation === 'vertical'
                                                    ? 'category'
                                                    : 'number'
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(item) =>
                                                data.orientation !== 'vertical'
                                                    ? formatNumber(item, data.valueFormat)
                                                    : item
                                            }
                                        />
                                        <Tooltip
                                            cursor={false}
                                            animationDuration={0}
                                            isAnimationActive={false}
                                            content={
                                                <TooltipCustom
                                                    tooltipTranslation={data.tooltipTranslation}
                                                    valueFormat={{ signDisplay: 'never' }}
                                                    tooltipValueTranslation={
                                                        data.tooltipValueTranslation
                                                    }
                                                    data={finalConfig}
                                                />
                                            }
                                        />
                                        {!data.isStacked && (
                                            <ReferenceLine
                                                x={data.orientation === 'vertical' ? 0 : null}
                                                y={data.orientation === 'horizontal' ? 0 : null}
                                                stroke={darkModeColorsChart[theme].referenceLine}
                                            />
                                        )}
                                        {data.graphData[0].avg && !hiddenKeys?.['avg'] && (
                                            <ReferenceLine
                                                y={data.graphData[0].avg}
                                                stroke={darkModeColorsChart[theme].referenceLine}
                                                strokeWidth={2}
                                            />
                                        )}
                                        {Object.entries(finalData[0]).map((item, index) => {
                                            if (
                                                (finalConfig.dataValuesProperties
                                                    ? finalConfig.dataValuesProperties.includes(
                                                          item[0]
                                                      )
                                                    : !ignoredData.includes(item[0])) &&
                                                !hiddenKeys?.[item[0]]
                                            ) {
                                                return (
                                                    <Bar
                                                        key={index}
                                                        isAnimationActive={!isExport}
                                                        name={
                                                            data.id === 'posts_distribution'
                                                                ? data.name[item[0]]
                                                                : t(data.name[item[0]])
                                                        }
                                                        dataKey={item[0]}
                                                        fill={
                                                            darkModeColorsChart[theme][
                                                                finalConfig.color[item[0]]
                                                            ]
                                                        }
                                                        stackId="stack"
                                                        shape={(props) => {
                                                            if (!data.isStacked) {
                                                                props.radius =
                                                                    data.orientation === 'vertical'
                                                                        ? [0, 2.5, 2.5, 0]
                                                                        : [2.5, 2.5, 0, 0];
                                                            }

                                                            return <Rectangle {...props} />;
                                                        }}
                                                    />
                                                );
                                            }
                                        })}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </SizeMe>
            )}
        </>
    );
};

HalfBarChartComponent.propTypes = {
    data: PropTypes.object,
    activeTable: PropTypes.bool,
    columnNames: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    activeFilter: PropTypes.string,
    height: PropTypes.number,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const HalfBarChart = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        legendType="square"
        {...restProps}>
        <HalfBarChartComponent />
    </ChartWrapper>
);

HalfBarChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { HalfBarChart, HalfBarChartComponent };
